/**
 * Team Manager (TM) types — /tm module
 */

// ─── TM User ──────────────────────────────────────────────────────────────────

export type TMUserStatus = 'pending' | 'approved' | 'rejected' | 'deactivated';
export type TMUserRole   = 'admin' | 'member';

export interface TMUserPermissions {
  createTask:   boolean;
  editTask:     boolean;
  updateTask:   boolean;   // update status / progress
  assignTask:   boolean;
  approveUsers: boolean;
}

export const DEFAULT_TM_PERMISSIONS: TMUserPermissions = {
  createTask:   false,
  editTask:     false,
  updateTask:   true,    // members can always update their own task progress
  assignTask:   false,
  approveUsers: false,
};

export interface TMUser {
  uid:         string;
  name:        string;
  email:       string;
  phone:       string;
  status:      TMUserStatus;
  role:        TMUserRole;
  permissions: TMUserPermissions;
  avatarUrl:   string | null;
  createdAt:   Date | null;
  approvedAt:  Date | null;
  approvedBy:  string | null;
}

export interface TMUserRef {
  uid:   string;
  name:  string;
  email: string;
  phone?: string;
}

// ─── Task ─────────────────────────────────────────────────────────────────────

export type TaskStatus   = 'todo' | 'in_progress' | 'review' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Task {
  id:           string;
  title:        string;
  description:  string | null;
  assignedTo:   TMUserRef | null;
  assignedBy:   TMUserRef;
  status:       TaskStatus;
  priority:     TaskPriority;
  dueDate:      Date | null;
  reminderDays: number;
  progress:     number;
  tags:         string[];
  createdAt:    Date | null;
  updatedAt:    Date | null;
}

// ─── Task History ─────────────────────────────────────────────────────────────

export type TaskHistoryAction =
  | 'created'
  | 'status_changed'
  | 'progress_changed'
  | 'assigned'
  | 'reassigned'
  | 'priority_changed'
  | 'due_date_changed'
  | 'edited'
  | 'comment';

export interface TaskHistoryChange {
  field:    string;
  oldValue: string | null;
  newValue: string | null;
}

export interface TaskHistoryEntry {
  id:        string;
  userId:    string;
  userName:  string;
  action:    TaskHistoryAction;
  comment:   string | null;
  changes:   TaskHistoryChange[];
  createdAt: Date | null;
}

export const TASK_HISTORY_LABELS: Record<TaskHistoryAction, string> = {
  created:          'Task created',
  status_changed:   'Status changed',
  progress_changed: 'Progress updated',
  assigned:         'Task assigned',
  reassigned:       'Task reassigned',
  priority_changed: 'Priority changed',
  due_date_changed: 'Due date changed',
  edited:           'Task edited',
  comment:          'Comment added',
};

// ─── Labels & colours ─────────────────────────────────────────────────────────

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo:        'To Do',
  in_progress: 'In Progress',
  review:      'Review',
  done:        'Done',
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  todo:        'bg-surface-100 text-surface-600',
  in_progress: 'bg-amber-100 text-amber-700',
  review:      'bg-blue-100 text-blue-700',
  done:        'bg-emerald-100 text-emerald-700',
};

export const TASK_STATUS_DOT: Record<TaskStatus, string> = {
  todo:        'bg-surface-400',
  in_progress: 'bg-amber-400',
  review:      'bg-blue-400',
  done:        'bg-emerald-400',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low:    'Low',
  medium: 'Medium',
  high:   'High',
  urgent: 'Urgent',
};

export const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  low:    'text-surface-500 bg-surface-100',
  medium: 'text-blue-600 bg-blue-50',
  high:   'text-amber-600 bg-amber-50',
  urgent: 'text-red-600 bg-red-50',
};

export const TASK_STATUSES:   TaskStatus[]   = ['todo', 'in_progress', 'review', 'done'];
export const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];
